import mergeClasses from '../../../utils/shallowMerge';
import { CarretArrowDown, TriangleDownFilled } from '../icons';
import InlineMessage from '../inlineMessage';
import { Option } from './Option';
import defaultClasses from './Select.module.scss';
import { SelectList } from './SelectList';
import { useSelect } from './useSelect';
import { SelectContext } from './useSelectContext';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { theme } from './_vars';

export const Select = (props) => {
  const {
    id = '',
    children,
    isReadOnly = false,
    isDisabled = false,
    isRequired = false,
    name = 'select',
    focused = true,
    labelPosition = 'normal',
    variant = 'normal',
    textAlign = 'left',
    iconBefore = '',
    iconAfter = '',
    label = '',
    error = false,
    errorMessage = '',
    className = '',
    selectHeight = '45px',
    customClasses,
    isPlaceholder = false,
    isOutOfStock = false,
    openToTop = false,
    customIcon: CustomIcon,
    dataAttributes,
    selectAriaLabel = '',
    register = (name, options) => ({ name, ...options }),
  } = props;

  const customProps = useSelect(props);
  const styles = mergeClasses(defaultClasses, customClasses);

  const providerProps = useMemo(
    () => ({
      ...props,
      ...customProps,
    }),
    [props, customProps],
  );

  const customInputClasses = clsx(
    styles.selectField,
    styles[variant],
    styles[`label_${labelPosition}`],
    {
      [` ${styles.focused}`]: focused,
      [` ${styles.textCentered}`]: textAlign === 'center',
      [` ${styles.withError}`]: error,
      [` ${styles.withIconLeft}`]: iconBefore,
      [` ${styles.withIconRight}`]: iconAfter,
    },
    className,
  );

  const { ref: formRef, ...formProps } = register(name, {
    onChange: customProps.handleInnerChange,
    disabled: isDisabled,
    required: isRequired,
  });

  return (
    <SelectContext.Provider value={providerProps}>
      <div style={{ '--selectHeight': selectHeight }} className={customInputClasses} ref={customProps.wrapperRef}>
        {label ? (
          <label htmlFor={id || name} className={styles.label}>
            {label}
          </label>
        ) : null}
        <div
          className={`${styles.fieldWrapper} ${styles.additionalField}`}
          ref={customProps.focusableRef}
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          onFocus={() => !isDisabled && customProps.setIsFocused(true)}
          onBlur={() => !isDisabled && customProps.setIsFocused(false)}
          onClick={(e) => !isDisabled && customProps.handleOuterClick(e)}
          onKeyDown={(e) => !isDisabled && customProps.handleOuterKeyDown(e)}
          {...dataAttributes}
        >
          <select
            tabIndex="-1"
            className={`${defaultClasses.selectFieldSelect} ${styles.selectDropdown}`}
            readOnly={isReadOnly}
            id={id || name}
            onMouseDown={customProps.handleInnerMouseDown}
            aria-label={selectAriaLabel}
            {...formProps}
            ref={(inputRef) => {
              if (formRef) {
                formRef(inputRef);
              }
              customProps.fieldRef.current = inputRef;
            }}
          >
            {children}
          </select>
          <div className={styles.after}>
            {theme !== 'Jag' ? (
              CustomIcon ? (
                <CustomIcon
                  style={customProps.isExpanded ? { transform: 'rotate(180deg)' } : { transform: 'rotate(0)' }}
                />
              ) : (
                <CarretArrowDown
                  height="30"
                  width="30"
                  style={customProps.isExpanded ? { transform: 'rotate(180deg)' } : { transform: 'rotate(0)' }}
                />
              )
            ) : (
              <TriangleDownFilled
                height="8"
                width="8"
                style={
                  customProps.isExpanded
                    ? { transform: 'rotate(180deg)', transition: 'all .3s' }
                    : { transform: 'rotate(0)' }
                }
              />
            )}
          </div>
        </div>
        {error && errorMessage ? (
          <InlineMessage type="error" message={errorMessage} className={styles.errorMessage} />
        ) : null}
        <SelectList isPlaceholder={isPlaceholder} isOutOfStock={isOutOfStock} openToTop={openToTop}>
          {children}
        </SelectList>
      </div>
    </SelectContext.Provider>
  );
};

Select.propTypes = {
  id: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOutOfStock: PropTypes.bool,
  placeholder: PropTypes.string,
  isReadOnly: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  name: PropTypes.string,
  variant: PropTypes.oneOf(['normal', 'underlined', 'filled']),
  labelPosition: PropTypes.oneOf(['normal', 'inset', 'dynamic']),
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
  iconBefore: PropTypes.node,
  iconAfter: PropTypes.node,
  label: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
  focused: PropTypes.bool,
  selectHeight: PropTypes.string,
  onSelect: PropTypes.func,
  openToTop: PropTypes.bool,
  dataAttributes: PropTypes.object,
  selectAriaLabel: PropTypes.string,
  register: PropTypes.func,
};

Select.Option = Option;
